import React, { useContext, useEffect } from 'react';
import { FormikTouched, FormikErrors } from 'formik';
import {
  Button,
  DatePicker,
  DatePickerInput,
  RadioButtonGroup,
  RadioButton,
  Link,
  Tooltip,
  MultiSelect,
  DropdownSkeleton
} from 'carbon-components-react';
import Utils from 'utilities/helper.util';
import { ArrowLeft16, ArrowRight16, Help16 } from '@carbon/icons-react';
import { LocalStoreContext } from '../../../localStore/context';
import { LodgeEnqyiryInitialValues } from '../../../localStore/types';

const HelpIcon = React.forwardRef(() => <Help16 />);
HelpIcon.displayName = 'HelpIcon';

const retiredExcavationTypes = ['SUBDIVISION'];

const JobDetailForm: React.FC<Props> = ({
  goBack,
  goNext,
  values,
  onClickAutoRenewBtn,
  autoRenewBtnDisabled,
  setFieldTouched,
  setFieldValue
}) => {
  const localStore = useContext(LocalStoreContext);
  const { designAct, excavationAct, essentialsLoading, earliestValidDate } =
    localStore;
  const jobStartMinDate = Utils.toLocalDateFormat(
    earliestValidDate || new Date()
  );
  const jobStartMaxDate = values.jobEndDate
    ? Utils.toLocalDateFormat(values.jobEndDate)
    : Utils.toLocalDateFormat(
        Utils.getNewDate().add(1, 'year').endOf('day').format()
      );
  const jobEndMinDate = values.jobStartDate
    ? Utils.toLocalDateFormat(values.jobStartDate)
    : Utils.toLocalDateFormat(new Date());
  const jobEndMaxDate = values.jobStartDate
    ? Utils.toLocalDateFormat(
        Utils.getNewDate(values.jobStartDate)
          .add(2, 'year')
          .endOf('day')
          .format()
      )
    : Utils.toLocalDateFormat(
        Utils.getNewDate().add(2, 'year').endOf('day').format()
      );
  const hasRetiredExcavationType =
    values.enquiryType === 'Excavation' &&
    Array.isArray(values.activityId) &&
    values.activityId.some((value: string) =>
      retiredExcavationTypes.includes(value)
    );

  const handleEnquiryTypeChange = (val: string) => {
    setFieldTouched('enquiryType', true);
    setFieldValue('enquiryType', val);
    setFieldValue('activityId', undefined);
  };

  const getSelectedItem = (list: any[], val: string | undefined) => {
    return val
      ? list.find((item) => item.value.toLowerCase() === val.toLowerCase())
      : undefined;
  };

  const onCalendarClose = (val: any, strValue: any, self: any) => {
    strValue && self.setDate(strValue, true, 'd/m/Y');
  };

  useEffect(() => {
    const earliestValidDateObj =
      Utils.getNewDate(earliestValidDate).startOf('day');
    if (values.jobStartDate < earliestValidDateObj) {
      setFieldValue('jobStartDate', '', true);
    }
    if (values.jobEndDate < earliestValidDateObj) {
      setFieldValue('jobEndDate', '', true);
    }
  }, [
    values.jobStartDate,
    values.jobEndDate,
    earliestValidDate,
    setFieldValue
  ]);

  useEffect(() => {
    if (hasRetiredExcavationType) {
      setFieldTouched('activityId', true);
      setFieldValue('activityId', [], true);
    }
  }, [hasRetiredExcavationType, setFieldTouched, setFieldValue]);

  return (
    <div className="enquiry-form--job">
      <div className="form-header">
        <Button
          className="back-btn"
          kind="ghost"
          renderIcon={ArrowLeft16}
          tooltipPosition="left"
          iconDescription="Back"
          hasIconOnly
          onClick={() => goBack()}
        />
        <Button
          className="continue-btn"
          kind="secondary"
          renderIcon={ArrowRight16}
          onClick={() => goNext()}
          disabled={
            !values.jobStartDate ||
            !values.jobEndDate ||
            !values.enquiryType ||
            !values.activityId ||
            hasRetiredExcavationType ||
            (Array.isArray(values.activityId) && values.activityId.length === 0)
          }
        >
          Continue
        </Button>
      </div>
      <div className="form-body">
        <div className="form-input-section">
          <div className="job-date-pickers">
            <DatePicker
              minDate={jobStartMinDate}
              maxDate={jobStartMaxDate}
              dateFormat="d/m/Y"
              datePickerType="single"
              onChange={(val: any) => {
                setFieldTouched('jobStartDate', true);
                setFieldValue('jobStartDate', val);
              }}
              value={
                values.jobStartDate
                  ? Utils.toLocalDateFormat(values.jobStartDate)
                  : ''
              }
              onClose={onCalendarClose}
            >
              <DatePickerInput
                id="jobStartDate"
                name="jobStartDate"
                data-testid="jobStartDate"
                labelText="Job start date"
                placeholder="dd/mm/yyyy"
                type="text"
                autoComplete="off"
              />
            </DatePicker>
            <DatePicker
              dateFormat="d/m/Y"
              datePickerType="single"
              minDate={jobEndMinDate}
              maxDate={jobEndMaxDate}
              onChange={(val: any) => {
                setFieldTouched('jobEndDate', true);
                setFieldValue('jobEndDate', val);
              }}
              value={
                values.jobEndDate
                  ? Utils.toLocalDateFormat(values.jobEndDate)
                  : ''
              }
              onClose={onCalendarClose}
            >
              <DatePickerInput
                id="jobEndDate"
                name="jobEndDate"
                labelText="Job end date"
                data-testid="jobEndDate"
                placeholder="dd/mm/yyyy"
                type="text"
                autoComplete="off"
              />
            </DatePicker>
          </div>
        </div>
        <div className="form-input-section">
          <div className="auto-renew-controls">
            <Link
              onClick={autoRenewBtnDisabled ? undefined : onClickAutoRenewBtn}
              disabled={autoRenewBtnDisabled}
            >
              Auto-renew properties
            </Link>
            <Tooltip
              align="end"
              renderIcon={HelpIcon}
              menuOffset={{
                left: 40
              }}
            >
              For longer jobs you have the option to automatically renew and get
              fresh plans every 28 days
            </Tooltip>
          </div>
        </div>
        <div className="form-input-section">
          <div className="bx--label">Enquiry type</div>
          <div className="enquiry-type-group">
            <RadioButtonGroup
              labelPosition="right"
              name="radio-button-group"
              onChange={handleEnquiryTypeChange}
              valueSelected={values.enquiryType}
            >
              <RadioButton
                id="radio-design"
                labelText="Design"
                value="Design"
              />
              <RadioButton
                id="radio-excavation"
                labelText="Excavation"
                value="Excavation"
              />
            </RadioButtonGroup>
          </div>
          {values.enquiryType && (
            <>
              {essentialsLoading ? (
                <DropdownSkeleton />
              ) : (
                <>
                  {designAct && values.enquiryType === 'Design' && (
                    <MultiSelect
                      id="activityTypes"
                      items={designAct}
                      itemToString={(item: any) => (item ? item.label : '')}
                      label="Activities"
                      titleText=""
                      initialSelectedItems={
                        Array.isArray(values.activityId)
                          ? values.activityId
                              .map((value: string) =>
                                getSelectedItem(designAct, value)
                              )
                              .filter(Boolean)
                          : []
                      }
                      onChange={({ selectedItems }: any) => {
                        setFieldTouched('activityId', true);
                        setFieldValue(
                          'activityId',
                          selectedItems.map((item: any) => item.value)
                        );
                      }}
                    />
                  )}
                  {excavationAct && values.enquiryType === 'Excavation' && (
                    <MultiSelect
                      id="activityTypes"
                      items={excavationAct}
                      itemToString={(item: any) => (item ? item.label : '')}
                      label="Activities"
                      titleText=""
                      initialSelectedItems={
                        Array.isArray(values.activityId) &&
                        !hasRetiredExcavationType
                          ? values.activityId
                              .map((value: string) =>
                                getSelectedItem(excavationAct, value)
                              )
                              .filter(Boolean)
                          : []
                      }
                      onChange={({ selectedItems }: any) => {
                        setFieldTouched('activityId', true);
                        setFieldValue(
                          'activityId',
                          selectedItems.map((item: any) => item.value)
                        );
                      }}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
      <div className="form-footer">
        <div className="form-progress--wrapper">
          <div className="form-progress completed"></div>
          <div className="form-progress completed"></div>
          <div className="form-progress"></div>
        </div>
      </div>
    </div>
  );
};

JobDetailForm.displayName = 'JobDetailForm';

type Props = {
  touched: FormikTouched<LodgeEnqyiryInitialValues>;
  errors: FormikErrors<LodgeEnqyiryInitialValues>;
  values: LodgeEnqyiryInitialValues;
  setFieldValue: Function;
  setFieldTouched: Function;
  onClickAutoRenewBtn: (e: React.MouseEvent) => void;
  autoRenewBtnDisabled: boolean;
  goNext: Function;
  goBack: Function;
};

export default React.memo(JobDetailForm);
